import Layout from 'Nori/Layout';
import Hero from 'Custom/Hero';
import HeroFull from 'Nori/Hero/HeroFull';
import RichText from 'Custom/RichText';
import Button from 'Nori/Button';
import SplitContent from 'Custom/Split/SplitContent';
import AjustableImage from 'Custom/AjustableImage';
import ConditionsTable from 'Custom/Table/ConditionsTable';
import Warnings from 'Custom/Warnings';
import HorizontalCard from 'Custom/Card/HorizontalCard';
import { Finder } from 'Custom/Finder';
import ItemBreakpoint from 'Custom/ItemBreakpoint/index';
import { CONDITIONS_TABLE } from 'Utils/replacing-implant';
import HERO_BG_IMAGE from 'Images/background/dark-blue-home.png';
import HERO_BG_IMAGE_MOBILE from 'Images/background/blue-bg-mobile.png';
import HeroQuestion from 'Custom/HeroQuestion';
import ARM_IMAGE_DESKTOP from 'Images/misc/arm-split-desktop.webp';
import ARM_IMAGE_MOBILE from 'Images/misc/arm-split-mobile.webp';
import BANDAGE_IMAGE_DESKTOP from 'Images/misc/bandage-desktop.webp';
import BANDAGE_IMAGE_MOBILE from 'Images/misc/bandage-mobile.webp';
import HAND_SHADOW from 'Images/misc/hand-shadow-new.png';
import HAND_SHADOW_MOBILE from 'Images/misc/hand-shadow-mobile.png';
import ArrowRight from 'Images/brand-icons/arrow-md-right.svg';
import CIRCLE_IMAGE_DESKTOP from 'Images/misc/circle-arrows-desktop.webp';
import CIRCLE_IMAGE_MOBILE from 'Images/misc/circle-arrows-mobile.webp';
import styles from 'styles/pages/replacingImplant.module.scss';

const HEAD = {
  title:
    'When to Replace Your NEXPLANON® (etonogestrel implant) 68 mg Radiopaque',
  description:
    'NEXPLANON® (etonogestrel implant) 68 mg Radiopaque must be removed or replaced after 5 years. Discover how the replacement process works.',
  keywords:
    'When to Replace Nexplanon, How to Replace Nexplanon Implant, Nexplanon Replacement',
};

const HERO_IMAGE = {
  src: HAND_SHADOW,
  alt: 'Hand Holding Implant Between Thumb and Index Finger to Show Relative Size of Implant',
};

const HERO_IMAGE_MOBILE = {
  src: HAND_SHADOW_MOBILE,
  alt: 'Hand Holding Implant Between Thumb and Index Finger to Show Relative Size of Implant',
};

const HeroBannerContent = () => {
  return (
    <Hero
      data-qa-tag="replacing-implant-hero-component"
      title={<>REPLACING YOUR IMPLANT</>}
      variant="overlap"
      caption="Implant not actual size."
      image={HERO_IMAGE}
      mobileImage={HERO_IMAGE_MOBILE}
      className={styles.whatIsHero}
      data-design-visible="visible_vd"
      data-design-type="module"
      data-design-nori_id="Hero"
      data-design-module_name="REPLACING YOUR IMPLANT"
      data-design-module_no="01"
    >
      <RichText data-qa-tag="replacing-implant-hero-content">
        <h2 data-qa-tag="replacing-implant-hero-title">
          <span>It’s year 5 and you’d like to continue using </span>
          <span className={styles.topColoredText}>NEXPLANON</span>{' '}
        </h2>
        <p
          className="body-text"
          data-qa-tag="replacing-implant-hero-description-a"
        >
          While you need to have your NEXPLANON implant removed by the end of
          the fifth year, you can also get a new implant inserted in the same
          removal procedure.
        </p>
        <p
          className="body-text"
          data-qa-tag="replacing-implant-hero-description-b"
        >
          Check with your healthcare provider for the date that NEXPLANON was
          inserted along with the date that it needs to be removed.
        </p>
        <p
          className="body-text"
          data-qa-tag="replacing-implant-hero-description-c"
        >
          Because your NEXPLANON implant must be removed by the end of the fifth
          year, it’s important for you to stay on top of your removal date.
        </p>
      </RichText>
    </Hero>
  );
};

export default function SideEffects() {
  return (
    <Layout head={HEAD}>
      <div
        className={styles.heroWrapper}
        data-qa-tag="replacing-implant-hero-wrapper"
      >
        <ItemBreakpoint breakpoint="lg">
          <HeroFull
            image={HERO_BG_IMAGE}
            data-qa-tag="replacing-implant-hero-image-desktop"
          >
            <HeroBannerContent></HeroBannerContent>
          </HeroFull>
          <HeroFull
            image={HERO_BG_IMAGE_MOBILE}
            data-qa-tag="replacing-implant-hero-mobile"
          >
            <HeroBannerContent></HeroBannerContent>
          </HeroFull>
        </ItemBreakpoint>
      </div>

      <SplitContent
        data-qa-tag="replacing-implant-circle-split"
        backgroundColor={'colored'}
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="set up an appointment with your doctor to remove NEXPLANON"
        data-design-module_no="02"
      >
        <ItemBreakpoint breakpoint="md">
          <AjustableImage
            data-qa-tag="replacing-implant-circle-image-desktop"
            size="full"
            image={{
              src: CIRCLE_IMAGE_DESKTOP,
              style: { width: 292 },
              alt: '',
            }}
          />
          <AjustableImage
            data-qa-tag="replacing-implant-circle-image-mobile"
            size="full"
            image={{ src: CIRCLE_IMAGE_MOBILE, alt: '' }}
          />
        </ItemBreakpoint>

        <>
          <RichText data-qa-tag="replacing-implant-circle-content">
            <h2 data-qa-tag="replacing-implant-circle-title" className="mb">
              Set up an appointment with a trained healthcare professional to
              remove NEXPLANON
            </h2>
            <p
              data-qa-tag="replacing-implant-circle-description-a"
              className="body-text"
            >
              A trained healthcare professional can remove NEXPLANON at any time
              during the 5-year period. If you want to keep it for 5 years,
              remember to check with your healthcare provider to confirm the
              date that it needs to be removed.
            </p>
            <p
              className="body-text"
              data-qa-tag="replacing-implant-circle-description-b"
            >
              Removal of the NEXPLANON implant involves a minor surgical
              procedure by a trained healthcare professional and can be
              performed in the office through a small incision in your arm where
              NEXPLANON is located.
            </p>
          </RichText>
          <Button
            data-qa-tag="replacing-implant-circle-button"
            type="simple"
            href="/removal/"
            data-design-type="clickAction"
            data-design-category="content_link_vd"
            data-design-action="Link"
            data-design-label="Learn about removal"
            data-design-nori_id="Inline Content"
            data-design-module_name="set up an appointment with your doctor to remove NEXPLANON"
            data-design-module_no="02"
          >
            Learn about removal <ArrowRight />
          </Button>
        </>
      </SplitContent>

      <SplitContent
        data-qa-tag="replacing-implant-arm-split"
        reverse
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="during the same visit, a new NEXPLANON can be reinserted into the same location"
        data-design-module_no="03"
      >
        <ItemBreakpoint breakpoint="md">
          <AjustableImage
            data-qa-tag="replacing-implant-arm-image-desktop"
            size="full"
            image={{ src: ARM_IMAGE_DESKTOP, style: { width: 292 }, alt: '' }}
          />
          <AjustableImage
            data-qa-tag="replacing-implant-arm-image-mobile"
            size="full"
            image={{ src: ARM_IMAGE_MOBILE, style: { width: 146 }, alt: '' }}
          />
        </ItemBreakpoint>
        <>
          <RichText data-qa-tag="replacing-implant-arm-content">
            <h2 data-qa-tag="replacing-implant-arm-title" className="mb">
              During the same visit, a new NEXPLANON can be reinserted into the
              same location
            </h2>
            <p
              className="body-text"
              data-qa-tag="replacing-implant-arm-description-a"
            >
              Since the area has already been sterilized and numbing medication
              has been administered, a trained healthcare professional can
              insert a new NEXPLANON into the same area of your inner upper arm.
            </p>
            <p
              data-qa-tag="replacing-implant-arm-description-b"
              className="body-text"
            >
              Once inserted, both the patient and the trained healthcare
              professional will then feel the area to ensure the implant is
              placed correctly. If it is, the healthcare professional will cover
              the site where NEXPLANON was placed with 2 bandages. Leave the top
              bandage on for 24 hours. Keep the bottom bandage clean, dry, and
              in place for 3 to 5 days.
            </p>
          </RichText>
          <Button
            data-qa-tag="replacing-implant-arm-button"
            type="simple"
            href="/insertion/"
            data-design-type="clickAction"
            data-design-category="content_link_vd"
            data-design-action="Link"
            data-design-label="Learn more about insertion"
            data-design-nori_id="Inline Content"
            data-design-module_name="during the same visit, a new NEXPLANON can be reinserted into the same location"
            data-design-module_no="03"
          >
            Learn more about insertion
            <ArrowRight />
          </Button>
        </>
      </SplitContent>

      <SplitContent
        data-qa-tag="replacing-implant-bandage-split"
        backgroundColor={'colored'}
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="following insertion, a trained healthcare professional will cover the insertion site with 2 bandages"
        data-design-module_no="04"
      >
        <ItemBreakpoint breakpoint="md">
          <AjustableImage
            data-qa-tag="replacing-implant-bandage-image-desktop"
            size="full"
            image={{
              src: BANDAGE_IMAGE_DESKTOP,
              style: { width: 292 },
              alt: '',
            }}
          />
          <AjustableImage
            data-qa-tag="replacing-implant-bandage-image-mobile"
            size="full"
            image={{ src: BANDAGE_IMAGE_MOBILE, alt: '' }}
          />
        </ItemBreakpoint>

        <>
          <RichText data-qa-tag="replacing-implant-bandage-content">
            <h2 data-qa-tag="replacing-implant-bandage-title" className="mb">
              Following insertion, a trained healthcare professional will cover
              the insertion site with 2 bandages
            </h2>
            <p
              data-qa-tag="replacing-implant-bandage-description"
              className="body-text"
            >
              Leave the top bandage on for 24 hours. Keep the bottom bandage
              clean, dry, and in place for 3 to 5 days.
            </p>
          </RichText>
          <Button
            className={styles.bandageButton}
            data-qa-tag="replacing-implant-bandage-button"
            type="simple"
            href="/insertion/"
            data-design-type="clickAction"
            data-design-category="content_link_vd"
            data-design-action="Link"
            data-design-label="See what to expect after insertion of NEXPLANON"
            data-design-nori_id="Inline Content"
            data-design-module_name="following insertion, a trained healthcare professional will cover the insertion site with 2 bandages"
            data-design-module_no="04"
          >
            See what to expect after insertion of NEXPLANON
            <ArrowRight />
          </Button>
        </>
      </SplitContent>

      <HorizontalCard
        data-qa-tag="replacing-implant-horizontal-section"
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="Already on nexplanon"
        data-design-module_no="05"
      />

      <Warnings
        data-qa-tag="replacing-implant-warning-section"
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="don’t use NEXPLANON if you …"
        data-design-module_no="06"
      />
      <ConditionsTable
        data-qa-tag="replacing-implant-condition-section"
        showIcon={false}
        {...CONDITIONS_TABLE}
        className={styles.conditionsTable}
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="tell your doctor if you have or have had any of these conditions"
        data-design-module_no="07"
      >
        <RichText data-qa-tag="replacing-implant-condition-section">
          <h2 data-qa-tag="replacing-implant-condition-title" className="mb">
            Tell your doctor if you have or have had any of these conditions
          </h2>
          <p
            data-qa-tag="replacing-implant-condition-description"
            className="intro"
          >
            Your healthcare professional can suggest a different method of birth
            control.
          </p>
        </RichText>
      </ConditionsTable>

      <Finder data-design-module_no="08" />

      <HeroQuestion
        data-design-module_no="09"
        data-qa-tag="replacing-implant-hero-question"
      ></HeroQuestion>
    </Layout>
  );
}
